import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const GOALSTYLE_VERSION_OPTION_POSITION = 1;

/**
 * Normalizes GoalStyle jersey version terminology across EN / ES / FR.
 * Women must be detected before Fan because "Women Fan" contains "Fan".
 * "Authentic" is treated as the media/SEO alias for Player.
 *
 * @param {string | null | undefined} value
 * @returns {'fan' | 'player' | 'women' | null}
 */
function normalizeGoalStyleVersion(value) {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  if (!normalized) return null;

  if (
    normalized.includes('women') ||
    normalized.includes('woman') ||
    normalized.includes('mujer') ||
    normalized.includes('femme') ||
    normalized.includes('feminin')
  ) {
    return 'women';
  }

  if (
    normalized.includes('authentic') ||
    normalized.includes('player') ||
    normalized.includes('jugador') ||
    normalized.includes('joueur')
  ) {
    return 'player';
  }

  if (normalized.includes('fan') || normalized.includes('aficionad')) return 'fan';

  return null;
}

/**
 * Finds the selected GoalStyle Version option. Version is option position 1,
 * while its value order can differ between products (for example Fan + Women Fan).
 * We therefore classify by the selected value, never by its index.
 *
 * @param {Document | Element | null | undefined} root
 * @returns {'fan' | 'player' | 'women' | null}
 */
function getSelectedGoalStyleVersion(root) {
  if (!root?.querySelector) return null;

  const selectedNode = root.querySelector(
    `variant-picker input:checked[data-input-id^="${GOALSTYLE_VERSION_OPTION_POSITION}-"],
     variant-picker option[selected][data-input-id^="${GOALSTYLE_VERSION_OPTION_POSITION}-"],
     variant-picker option:checked[data-input-id^="${GOALSTYLE_VERSION_OPTION_POSITION}-"]`,
  );

  if (!(selectedNode instanceof HTMLElement)) return null;

  return normalizeGoalStyleVersion(selectedNode.getAttribute('value') || selectedNode.textContent);
}

/**
 * Infers the media family from filename/URL or ALT text.
 * Unclassified media is considered generic and remains visible for every version.
 *
 * @param {Element} node
 * @returns {'fan' | 'player' | 'women' | null}
 */
function getGoalStyleMediaVersion(node) {
  const images = node instanceof HTMLImageElement ? [node] : Array.from(node.querySelectorAll('img'));
  if (!images.length) return null;

  const searchable = images
    .flatMap((image) => [image.alt, image.currentSrc, image.src, image.srcset])
    .filter(Boolean)
    .join(' ');

  return normalizeGoalStyleVersion(searchable);
}

/**
 * Rewrites Horizon's index-based custom-element event handlers after media nodes are removed.
 *
 * @param {Element} node
 * @param {number} index
 */
function rewriteIndexedActions(node, index) {
  const indexedActionPattern = /\/(open|select|preloadImage|zoom|handleThumbnailClick|handleThumbnailPointerEnter)\/\d+/g;

  for (const attribute of ['on:click', 'on:pointerenter']) {
    const value = node.getAttribute(attribute);
    if (!value) continue;

    node.setAttribute(attribute, value.replace(indexedActionPattern, (_match, action) => `/${action}/${index}`));
  }
}

/**
 * Keeps only the selected GoalStyle media family plus genuinely generic media.
 * This prevents Fan, Player and Women Fan imagery from leaking into one another.
 *
 * @param {Element} gallery
 * @param {'fan' | 'player' | 'women' | null} version
 */
function filterGoalStyleGallery(gallery, version) {
  if (!version) return;

  const slidesBeforeFiltering = Array.from(gallery.querySelectorAll('slideshow-slide'));
  const hasRequestedVersion = slidesBeforeFiltering.some((slide) => getGoalStyleMediaVersion(slide) === version);

  // Fail safely for products that do not use GoalStyle's media naming convention.
  if (!hasRequestedVersion) return;

  const versionedSelectors = [
    'slideshow-slide',
    '.media-gallery__grid > li',
    '.slideshow-controls__thumbnail',
    '.dialog-thumbnails-list__thumbnail',
    '.dialog-zoomed-gallery > li',
  ];

  for (const selector of versionedSelectors) {
    for (const node of Array.from(gallery.querySelectorAll(selector))) {
      const mediaVersion = getGoalStyleMediaVersion(node);
      if (mediaVersion && mediaVersion !== version) node.remove();
    }
  }

  const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
  const slideCount = slides.length;

  slides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    rewriteIndexedActions(slide, index);

    const style = slide.getAttribute('style');
    if (style) {
      slide.setAttribute(
        'style',
        style.replace(/--slideshow-timeline:\s*--slide-\d+/g, `--slideshow-timeline: --slide-${index}`),
      );
    }
  });

  for (const [index, gridItem] of Array.from(gallery.querySelectorAll('.media-gallery__grid > li')).entries()) {
    for (const actionNode of gridItem.querySelectorAll('[on\\:click], [on\\:pointerenter]')) {
      rewriteIndexedActions(actionNode, index);
    }
  }

  for (const controls of gallery.querySelectorAll('slideshow-controls')) {
    const thumbnails = Array.from(controls.querySelectorAll('.slideshow-controls__thumbnail'));

    thumbnails.forEach((thumbnail, index) => {
      rewriteIndexedActions(thumbnail, index);
      if (index === 0) thumbnail.setAttribute('aria-selected', 'true');
      else thumbnail.removeAttribute('aria-selected');
    });

    const dots = Array.from(controls.querySelectorAll('.slideshow-controls__dots > li'));
    dots.forEach((dot, index) => {
      if (index >= slideCount) {
        dot.remove();
        return;
      }

      const button = dot.querySelector('button');
      if (!button) return;

      rewriteIndexedActions(button, index);
      button.style.animationTimeline = `--slide-${index}`;
      if (index === 0) button.setAttribute('aria-selected', 'true');
      else button.removeAttribute('aria-selected');
    });

    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter) {
      counter.innerHTML = `<span ref="current">1</span><span class="slash">/</span>${slideCount}`;
    }

    if (slideCount <= 1) controls.remove();
  }

  const slideshow = gallery.querySelector('slideshow-component');
  if (slideshow instanceof HTMLElement) {
    slideshow.style.setProperty(
      '--slideshow-timeline',
      Array.from({ length: slideCount }, (_item, index) => `--slide-${index}`).join(','),
    );

    if (slideCount <= 1) {
      slideshow.removeAttribute('infinite');
      slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
      slideshow.querySelectorAll('slideshow-arrows, .slideshow-controls__arrows').forEach((node) => node.remove());
    }
  }

  const zoomThumbnails = Array.from(gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
  zoomThumbnails.forEach((thumbnail, index) => {
    rewriteIndexedActions(thumbnail, index);
    if (index === 0) thumbnail.setAttribute('aria-selected', 'true');
    else thumbnail.removeAttribute('aria-selected');
  });

  gallery.dataset.goalstyleVersionFiltered = version;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    if (!this.dataset.goalstyleVersionFiltered) {
      const productContext = this.closest('.shopify-section, dialog') ?? document;
      filterGoalStyleGallery(this, getSelectedGoalStyleVersion(productContext));
    }

    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Replaces the gallery after a variant selection and filters the fresh HTML
   * before it becomes visible, avoiding flashes of the wrong version.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const liveContext = event.target.closest('.shopify-section, dialog');
        const selectedVersion = getSelectedGoalStyleVersion(html) ?? getSelectedGoalStyleVersion(liveContext);
        filterGoalStyleGallery(newMediaGallery, selectedVersion);

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /** @param {ZoomMediaSelectedEvent} event */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /** @param {number} index @param {PointerEvent} event */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /** @param {number} index */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
